import {
  RESET_ROW_RESULTS,
  CLEAR_ALL,
  SET_CONFIG,
  SET_FACETS_DATA,
  SET_FACETS_SEARCHBAR,
  SET_IS_FAVORITE,
  SET_LOADING_FACETS,
  SET_LOADING_RESULTS,
  SET_PAGE,
  SET_PAGE_DATA,
  SET_PARAM,
  SET_PARAMS,
  SET_PER_PAGE,
  SET_RESULTS_DATA,
  SET_SELLERS_DATA,
  SET_VIEW_TYPE,
  MODIFY_ROW_TOUCHED,
  MODIFY_ROW_DELETED,
  MODIFY_ROW_RESTORED,
  MODIFY_ROW_REMOVED,
  DECREMENT_PAGINATION_TOTAL,
  MODIFY_ROW_HIDDEN,
  MODIFY_ROW_SHOWN,
  MODIFY_ROW_NON_PUBLIC,
  MODIFY_ROW_PUBLIC,
  SET_SHOW_SELLERS,
  SET_SHOW_MOBILE_FACETS,
  MODIFY_ROW_PARKED,
  SET_CURRENT_ROUTE,
  MODIFY_ROW_PROMOTION_ACTIVE,
  SET_PREVIOUS_ROUTE,
  SET_MAP_DATA,
  CLEAR_MAP_DATA,
  SET_MAP_SEARCH_FLAG,
  SET_SELLERS_ONLY_FLAG,
  SET_LOADING_MAP,
  SET_CLUSTER_RESULTS_DATA,
  SET_LOADING_CLUSTER_RESULTS,
  SET_CLASSIFIED_PAID_INFO,
  SET_PAID_SUMMARY,
  SET_MASS_ACTIONS_REMAINING_ACTIONS,
  SET_MASS_ACTIONS_TOTAL_ACTIONS,
  SET_MASS_ACTIONS_RESET_TIMESTAMP,
  SET_AUTO_TOUCH_DATA_IN_SEARCH,
  SET_SCROLL_TOP,
  MODIFY_ROW_IN_COMPARISON,
  MODIFY_COMPARE_IDS,
  MODIFY_ROW_VISIBILITY,
  MODIFY_ROW_PUBLIC_STATUS,
  NULLIFY_ROW_SUGGESTED_CATEGORY
} from './mutation-types'
import { SearchRoute, SearchState } from './state'
import {
  Category,
  Facet,
  FacetsResult,
  ResultsResult,
  SellersResult
} from '~/models/search/types'
import { CompactPage } from '~/models/common/types'
import { MutationTree } from 'vuex'
import { ViewType } from '~/models/shared/types'
import { PaidClassifiedsInfoSummary } from '~/models/classified/paid/types'
import { AutoTouchSchedule } from '~/models/auto-touch/types'
import { SearchConfig } from '~/models/search/config'
import { compatibilityParamsMappings } from '~/constants/classified/search/param'
import { vue3Delete, vue3Set } from '~/utils/nuxt3-migration'

export function preprocessParamValue(value: any) {
  if (Array.isArray(value)) {
    return value
      .filter(v => v !== null && v !== undefined)
      .map(v => v.toString())
  } else if (
    value === null ||
    typeof value === 'undefined' ||
    value === false
  ) {
    return value
  }
  return [value.toString()]
}

export function preprocessRoute(route: SearchRoute): SearchRoute {
  const { query, path } = route
  delete query.sp
  return { query, path }
}

export default {
  [RESET_ROW_RESULTS](state: SearchState) {
    state.rows = null
  },
  [SET_CURRENT_ROUTE](state, currentRoute: SearchRoute) {
    state.currentRoute = preprocessRoute(currentRoute)
  },
  [SET_PREVIOUS_ROUTE](state, previousRoute: SearchRoute) {
    state.previousRoute = preprocessRoute(previousRoute)
  },
  [SET_PAGE_DATA](state: SearchState, pageData: CompactPage) {
    state.page = pageData
  },
  [SET_LOADING_FACETS](state: SearchState, loading: boolean) {
    state.loading.facets = loading
  },
  [SET_LOADING_RESULTS](state: SearchState, loading: boolean) {
    state.loading.results = loading
  },
  [SET_LOADING_MAP](state: SearchState, loading: boolean) {
    state.loading.map = loading
  },
  [SET_LOADING_CLUSTER_RESULTS](state: SearchState, loading: boolean) {
    state.loading.clusterResults = loading
  },
  [SET_RESULTS_DATA](state: SearchState, data: ResultsResult) {
    state.newWantedClassifiedUrl = data.newWantedClassifiedUrl
    state.showInPlotUrl = data.showInPlotUrl
    state.moreResultsButton = data.results.moreResultsButton
    state.rows = data.results.rows
    state.compare = data.compare
    state.savable = data.results.savable
    state.shareable = data.results.shareable
    state.pagination = data.results.pagination
    state.sellersInfo = data.sellers
    state.backendAdsConfig = data.results.ads
  },
  [SET_CLUSTER_RESULTS_DATA](state: SearchState, data: ResultsResult) {
    state.clusterRows = data.results.rows
    state.clusterPagination = data.results.pagination
  },
  [SET_FACETS_DATA](state: SearchState, data: FacetsResult) {
    state.facets = data.facets
    state.category = data.category
    state.categoryIds = data.categoryIds
    state.adsTargetings = data.adsTargetings
    state.args = data.args
    state.canBeSaved = data.canBeSaved
    state.changeSearchUrl = data.changeSearchUrl
    state.isFavorite = data.isFavorite
    state.longTitle = data.longTitle
    state.massActions = data.massActions || {}
    state.mySearchLinks = data.mySearchLinks?.searchLinks
    state.newClassifiedUrl = data.newClassifiedUrl
    state.mapSearchUrl = data.mapSearchUrl
    state.previousSearch = data.previousSearch
    state.userProfile = data.userProfile
    state.searchId = data.searchId
    state.shortTitle = data.shortTitle
    state.sortFields = data.sortFields
    vue3Set(state, 'stats', data.stats) // to deal with reactivity issues
    state.title = data.title
    state.textSearch = data.textSearch
    state.notifications = {
      bulkValid: data.searchSubscription?.search?.notifications?.bulkValid,
      instantValid: data.searchSubscription?.search?.notifications?.instantValid
    }
    state.userOwnsSearch = data.userOwnsSearch
    state.notMyClassifiedsUrl = data.notMyClassifiedsUrl
  },
  [SET_VIEW_TYPE](state: SearchState, viewType: ViewType) {
    state.viewType = viewType
  },
  [SET_PER_PAGE](state: SearchState, value: number) {
    state.perPage = value
  },
  [SET_CONFIG](state: SearchState, config: SearchConfig) {
    state.config = config
  },
  [SET_IS_FAVORITE](state: SearchState, value: boolean) {
    state.isFavorite = value
  },
  [SET_PARAMS](state: SearchState, params: any) {
    const formattedParams = Object.keys(params).reduce((acc: any, key) => {
      // comment out if we ever want to transform region/location stuff here
      // const keyToUse = compatibilityParamsMappings[key] || key
      acc[key] = preprocessParamValue(params[key])
      return acc
    }, {})
    state.params = formattedParams
  },
  [SET_PARAM](
    state: SearchState,
    { name, value }: { name: string; value: any }
  ) {
    const nameToUse = compatibilityParamsMappings[name] || name
    if (value === null || value === undefined) {
      vue3Delete(state.params, nameToUse)
      return
    }
    vue3Set(state.params, nameToUse, preprocessParamValue(value))
  },
  [SET_FACETS_SEARCHBAR](
    state: SearchState,
    { q, category }: { q: string; category: any }
  ) {
    const searchbarFacet: Facet | undefined =
      state.facets.find(facet => facet.name === 'mein') ||
      state.facets.find(facet => facet.name === 'admin_mein') ||
      state.facets.find(facet => facet.name === 'long_mein')
    if (!(searchbarFacet && searchbarFacet.values && searchbarFacet.category)) {
      return
    }
    if (q || q === '') {
      searchbarFacet.values.value = q
    }
    if (category) {
      searchbarFacet.category.id = category
    }
  },
  [CLEAR_ALL](state: SearchState, clearFacets: boolean = false) {
    state.params = {}
    if (clearFacets) {
      state.facets = []
    }
  },
  [SET_CLASSIFIED_PAID_INFO](
    state,
    {
      classifiedId,
      paidInfo
    }: {
      classifiedId: number
      paidInfo: {
        requiresPayment: boolean
        hasPaymentFailure: boolean
      }
    }
  ) {
    const row = state.rows?.find(row => row.id === classifiedId)
    if (row) {
      row.paidClassified = paidInfo
    }
  },
  [SET_PAGE](state: SearchState, page: number) {
    state.params.pg = preprocessParamValue(page)
    state.pagination.page = page
  },
  [SET_SELLERS_DATA](state, data: SellersResult) {
    const { isFavorite, sellers } = data
    state.isFavorite = isFavorite
    state.pagination = sellers.pagination
    state.sellerRows = sellers.rows
    state.sellersClassifiedsInfo = sellers.classifieds
  },
  [SET_MAP_DATA](state, data: { map: { clusters: []; markers: [] } }) {
    const { clusters, markers } = data.map
    state.map.clusters = clusters
    state.map.markers = markers
  },
  [CLEAR_MAP_DATA](state) {
    state.map.clusters = []
    state.map.markers = []
  },
  [MODIFY_ROW_PARKED](state, { classifiedId, parked }) {
    const row = state.rows && state.rows.find(row => row.id === classifiedId)
    if (state.clusterRows?.length) {
      const clusterRow = state.clusterRows.find(row => row.id === classifiedId)
      if (clusterRow) {
        clusterRow.isParked = parked
      }
    }
    if (row) {
      row.isParked = parked
    }
  },
  [MODIFY_ROW_TOUCHED](state, { classifiedId, modified }) {
    const row = state.rows && state.rows.find(row => row.id === classifiedId)
    if (row) {
      row.modified = modified || Date.now().toString()
      if (row.touch) {
        row.touch.available = false
      }
      row.stateColor = 'green'
      vue3Set(row, 'touched', true)
    }
  },
  [MODIFY_ROW_PROMOTION_ACTIVE](state, { classifiedId, promo }) {
    const row = state.rows && state.rows.find(row => row.id === classifiedId)
    if (row && row.promotion) {
      row.promotion.activePromotion = promo
    }
  },
  [MODIFY_ROW_DELETED](state, { classifiedId, deletedDate }) {
    const row = state.rows && state.rows.find(row => row.id === classifiedId)
    if (row) {
      row.isDeleted = true
      row.states.isDeleted = true
      row.deletedDate = deletedDate || Date.now().toString()
    }
  },
  [NULLIFY_ROW_SUGGESTED_CATEGORY](
    state,
    {
      classifiedId,
      acceptedCategory
    }: { classifiedId: number; acceptedCategory: Category | null }
  ) {
    const row = state.rows && state.rows.find(row => row.id === classifiedId)
    if (row) {
      row.suggestedCategory = null

      if (acceptedCategory) {
        row.category = acceptedCategory.humanPath
      }
    }
  },
  [MODIFY_ROW_RESTORED](state, { classifiedId }) {
    const row = state.rows && state.rows.find(row => row.id === classifiedId)
    if (row) {
      row.isDeleted = false
      row.states.isDeleted = false
      row.states.isRemoved = false
      row.deletedDate = ''
    }
  },
  [MODIFY_ROW_REMOVED](state, { classifiedId }) {
    const row = state.rows && state.rows.find(row => row.id === classifiedId)
    if (row) {
      row.states.isRemoved = true
    }
  },
  [DECREMENT_PAGINATION_TOTAL](state) {
    state.pagination.total--
  },
  [MODIFY_ROW_HIDDEN](state, { classifiedId }) {
    const row = state.rows && state.rows.find(row => row.id === classifiedId)
    if (row) {
      row.states.isHidden = true
    }
  },
  [MODIFY_ROW_SHOWN](state, { classifiedId }) {
    const row = state.rows && state.rows.find(row => row.id === classifiedId)
    if (row) {
      row.states.isHidden = false
    }
  },
  [MODIFY_ROW_NON_PUBLIC](state, { classifiedId }) {
    const row = state.rows && state.rows.find(row => row.id === classifiedId)
    if (row) {
      row.states.isNonPublic = true
    }
  },
  [MODIFY_ROW_PUBLIC](state, { classifiedId }) {
    const row = state.rows && state.rows.find(row => row.id === classifiedId)
    if (row) {
      row.states.isNonPublic = false
    }
  },
  [SET_SHOW_SELLERS](state, showSellersState: boolean) {
    vue3Set(state.params, 'show-sellers', showSellersState ? true : null)
  },
  [SET_SHOW_MOBILE_FACETS](state, mobileFacetsState: boolean) {
    state.showMobileFacets = mobileFacetsState
  },
  [SET_MAP_SEARCH_FLAG](state, value: boolean) {
    state.flags.isMapSearch = value
  },
  [SET_SELLERS_ONLY_FLAG](state, value: boolean) {
    state.flags.isSellersOnlySearch = value
  },
  [SET_PAID_SUMMARY](state, value: PaidClassifiedsInfoSummary) {
    state.paidSummary = value
  },
  [SET_MASS_ACTIONS_REMAINING_ACTIONS](state, value: number) {
    if (state.massActions) {
      state.massActions.remainingActions = value
    }
  },
  [SET_MASS_ACTIONS_TOTAL_ACTIONS](state, value: number) {
    if (state.massActions) {
      state.massActions.totalActions = value
    }
  },
  [SET_MASS_ACTIONS_RESET_TIMESTAMP](state, value: string) {
    if (state.massActions) {
      state.massActions.resetTimestamp = value
    }
  },
  [SET_AUTO_TOUCH_DATA_IN_SEARCH](
    state,
    {
      classifiedId,
      autoTouchData
    }: { classifiedId: number; autoTouchData: AutoTouchSchedule | null }
  ) {
    const classified = state.rows?.find(r => r.id === classifiedId)
    if (classified) {
      vue3Set(classified, 'autoTouch', autoTouchData)
    }
  },
  [SET_SCROLL_TOP](state, scrollTop: number) {
    state.scrollTop = scrollTop || 0
  },
  [MODIFY_ROW_IN_COMPARISON](
    state,
    {
      classifiedId,
      isInComparison
    }: { classifiedId: number; isInComparison: boolean }
  ) {
    const row = state.rows && state.rows.find(row => row.id === classifiedId)
    if (row) {
      row.compare.isInComparison = isInComparison
    }
  },
  [MODIFY_COMPARE_IDS](state, ids: number[]) {
    state.compare.ids = ids
  },
  [MODIFY_ROW_VISIBILITY](
    state,
    { classifiedId, isHidden }: { classifiedId: number; isHidden: boolean }
  ) {
    const row = state.rows?.find(row => row.id === classifiedId)

    if (row) {
      row.states.isHidden = isHidden
    }
  },
  [MODIFY_ROW_PUBLIC_STATUS](
    state,
    {
      classifiedId,
      isNonPublic
    }: { classifiedId: number; isNonPublic: boolean }
  ) {
    const row = state.rows?.find(row => row.id === classifiedId)

    if (row) {
      row.states.isNonPublic = isNonPublic
    }
  }
} as MutationTree<SearchState>
